import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CATEGORIES, STATUSES, LANTAI_OPTIONS, KONDISI_OPTIONS, TRIWULAN_OPTIONS, photoSrc, formatRp, itemCost, jobCost } from "../lib/constants";
import { createJob, updateJob, uploadPhoto, aiRecommend } from "../lib/api";

const emptyItem = () => ({ gedung: "", lantai: "", ruang: "", kategori: "AC", kondisi: "", spesifikasi: "", detail: "", bahan: "", status: "Belum Dikerjakan", photos: [], costs: [], _uploading: false });

// build <option> list ensuring current value is present even if not in master
const withCurrent = (opts, current) => (current && !opts.includes(current) ? [current, ...opts] : opts);

export const JobFormModal = ({ open, initialJob, locations, buildings = [], prices = [], onClose, onSaved }) => {
  const [locationId, setLocationId] = useState("");
  const [title, setTitle] = useState("");
  const [triwulan, setTriwulan] = useState("");
  const [tahun, setTahun] = useState("");
  const [items, setItems] = useState([emptyItem()]);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initialJob;

  useEffect(() => {
    if (!open) return;
    if (initialJob) {
      setLocationId(initialJob.location_id);
      setTitle(initialJob.title);
      setTriwulan(initialJob.triwulan ? String(initialJob.triwulan) : "");
      setTahun(initialJob.tahun ? String(initialJob.tahun) : "");
      setItems(initialJob.items.map((i) => ({ ...emptyItem(), ...i, _uploading: false })));
    } else {
      setLocationId(locations[0]?.id || "");
      setTitle("");
      setTriwulan("");
      setTahun(String(new Date().getFullYear()));
      setItems([emptyItem()]);
    }
  }, [open, initialJob, locations]);

  if (!open) return null;

  const locBuildings = buildings.filter((b) => b.location_id === locationId);
  const gedungOptions = locBuildings.map((b) => b.name);
  const roomsOf = (gedung) => (locBuildings.find((b) => b.name === gedung)?.rooms || []);
  const floorsOf = (gedung) => {
    const f = locBuildings.find((b) => b.name === gedung)?.floors || [];
    return f.length ? f : LANTAI_OPTIONS;
  };

  const updateItem = (idx, patch) => setItems((p) => p.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () => setItems((p) => [...p, emptyItem()]);
  const removeItem = (idx) => setItems((p) => (p.length > 1 ? p.filter((_, i) => i !== idx) : p));

  const onPhotos = async (idx, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    updateItem(idx, { _uploading: true });
    try {
      const paths = [];
      for (const f of files) paths.push(await uploadPhoto(f));
      setItems((p) => p.map((it, i) => (i === idx ? { ...it, photos: [...(it.photos || []), ...paths], _uploading: false } : it)));
    } catch {
      updateItem(idx, { _uploading: false });
      toast.error("Gagal mengunggah foto");
    }
  };
  const removePhoto = (idx, pi) =>
    setItems((p) => p.map((it, i) => (i === idx ? { ...it, photos: it.photos.filter((_, k) => k !== pi) } : it)));

  const addCost = (idx, priceId) => {
    const pr = prices.find((x) => x.id === priceId);
    if (!pr) return;
    setItems((p) => p.map((it, i) => (i === idx ? { ...it, costs: [...(it.costs || []), { label: pr.name, type: pr.type, price: pr.price, qty: 1 }] } : it)));
  };
  const setCostQty = (idx, ci, qty) =>
    setItems((p) => p.map((it, i) => (i === idx ? { ...it, costs: it.costs.map((c, k) => (k === ci ? { ...c, qty: Number(qty) || 0 } : c)) } : it)));
  const removeCost = (idx, ci) =>
    setItems((p) => p.map((it, i) => (i === idx ? { ...it, costs: it.costs.filter((_, k) => k !== ci) } : it)));

  const aiSuggest = async (idx) => {
    const it = items[idx];
    updateItem(idx, { _ai: true });
    try {
      const r = await aiRecommend({ kategori: it.kategori, spesifikasi: it.spesifikasi, kondisi: it.kondisi, kendala: it.kendala, detail: it.detail });
      setItems((p) => p.map((x, i) => (i === idx ? { ...x, detail: r.detail || x.detail, bahan: r.bahan && r.bahan !== "-" ? r.bahan : x.bahan, _ai: false } : x)));
      toast.success("Saran AI diterapkan ke Detail & Bahan");
    } catch {
      updateItem(idx, { _ai: false });
      toast.error("Gagal mengambil saran AI");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!locationId || !title) return toast.error("Lengkapi lokasi dan judul pekerjaan");
    const payload = {
      location_id: locationId,
      title,
      triwulan: triwulan ? Number(triwulan) : null,
      tahun: tahun ? Number(tahun) : null,
      items: items.map(({ _uploading, _ai, ...rest }) => rest),
    };
    setSaving(true);
    try {
      if (isEdit) await updateJob(initialJob.id, payload);
      else await createJob(payload);
      toast.success("Data pekerjaan tersimpan");
      onSaved();
    } catch {
      toast.error("Gagal menyimpan data");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-teal-500 outline-none";
  const selCls = `${inputCls} bg-white font-semibold`;

  return (
    <div className="no-print fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-auto overflow-hidden flex flex-col max-h-[92vh] animate-fade-up" data-testid="job-form-modal">
        <div className="px-6 py-4 bg-gradient-to-r from-slate-900 to-teal-800 text-white flex justify-between items-center">
          <h3 className="text-base sm:text-lg font-bold flex items-center gap-2">
            <i className="ph-bold ph-note-pencil text-teal-300" /> {isEdit ? "Edit Pekerjaan" : "Tambah Pekerjaan"}
          </h3>
          <button onClick={onClose} className="text-slate-300 hover:text-white p-1 rounded-lg hover:bg-white/10" data-testid="job-form-close"><i className="ph-bold ph-x text-xl" /></button>
        </div>

        <form onSubmit={submit} className="p-6 overflow-y-auto space-y-5 flex-1">
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Lokasi / Pelanggan <span className="text-red-500">*</span></label>
              <select value={locationId} onChange={(e) => setLocationId(e.target.value)} className={selCls} data-testid="job-location-select">
                {locations.length === 0 && <option value="">(Belum ada lokasi)</option>}
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Judul Pekerjaan <span className="text-red-500">*</span></label>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="mis. Gedung Joeang" className={inputCls} data-testid="job-title" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Triwulan</label>
              <select value={triwulan} onChange={(e) => setTriwulan(e.target.value)} className={selCls} data-testid="job-triwulan-select">
                <option value="">— Pilih Triwulan —</option>
                {TRIWULAN_OPTIONS.map((t) => <option key={t} value={t}>Triwulan {t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Tahun</label>
              <input type="number" value={tahun} onChange={(e) => setTahun(e.target.value)} placeholder="mis. 2026" min="2000" max="2100" className={inputCls} data-testid="job-tahun" />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">Item Pekerjaan</h4>
              <button type="button" onClick={addItem} className="inline-flex items-center gap-1.5 text-xs font-bold bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100 px-3 py-1.5 rounded-lg" data-testid="add-item-btn">
                <i className="ph-bold ph-plus-circle text-base" /> Tambah Item
              </button>
            </div>
            <div className="space-y-4">
              {items.map((item, idx) => (
                <div key={idx} className="p-4 bg-white rounded-xl border border-slate-200 shadow-sm space-y-3" data-testid={`item-row-${idx}`}>
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-slate-400">Item #{idx + 1}</span>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => aiSuggest(idx)} disabled={item._ai} className="inline-flex items-center gap-1 text-[11px] font-bold bg-gradient-to-r from-indigo-500 to-violet-500 text-white px-2.5 py-1 rounded-lg shadow-sm hover:opacity-90 disabled:opacity-60" data-testid={`item-ai-${idx}`}>
                        <i className={`ph-bold ${item._ai ? "ph-spinner animate-spin" : "ph-sparkle"}`} /> {item._ai ? "Memproses..." : "Saran AI"}
                      </button>
                      {items.length > 1 && <button type="button" onClick={() => removeItem(idx)} className="text-rose-500 hover:text-rose-700 text-xs font-semibold"><i className="ph-bold ph-trash" /> Hapus</button>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 mb-1">Gedung</label>
                      <select value={item.gedung} onChange={(e) => updateItem(idx, { gedung: e.target.value, ruang: "", lantai: "" })} className={selCls} data-testid={`item-gedung-${idx}`}>
                        <option value="">— Pilih Gedung —</option>
                        {withCurrent(gedungOptions, item.gedung).map((g) => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 mb-1">Lantai</label>
                      <select value={item.lantai} onChange={(e) => updateItem(idx, { lantai: e.target.value })} className={selCls} data-testid={`item-lantai-${idx}`}>
                        <option value="">— Pilih Lantai —</option>
                        {withCurrent(floorsOf(item.gedung), item.lantai).map((l) => <option key={l} value={l}>{l}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 mb-1">Ruang</label>
                      <select value={item.ruang} onChange={(e) => updateItem(idx, { ruang: e.target.value })} className={selCls} data-testid={`item-ruang-${idx}`}>
                        <option value="">— Pilih Ruang —</option>
                        {withCurrent(roomsOf(item.gedung), item.ruang).map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 mb-1">Kategori</label>
                      <select value={item.kategori} onChange={(e) => updateItem(idx, { kategori: e.target.value })} className={selCls} data-testid={`item-kategori-${idx}`}>
                        {withCurrent(CATEGORIES, item.kategori).map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 mb-1">Kondisi</label>
                      <select value={item.kondisi} onChange={(e) => updateItem(idx, { kondisi: e.target.value })} className={selCls} data-testid={`item-kondisi-${idx}`}>
                        <option value="">— Tidak diisi —</option>
                        {withCurrent(KONDISI_OPTIONS, item.kondisi).map((k) => <option key={k} value={k}>{k}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 mb-1">Status</label>
                      <select value={item.status} onChange={(e) => updateItem(idx, { status: e.target.value })} className={selCls} data-testid={`item-status-${idx}`}>
                        {withCurrent(STATUSES, item.status).map((st) => <option key={st} value={st}>{st}</option>)}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Spesifikasi</label>
                    <textarea value={item.spesifikasi} onChange={(e) => updateItem(idx, { spesifikasi: e.target.value })} rows={2} placeholder="mis. Panasonic 2 PK (Normal Dingin)" className={inputCls} data-testid={`item-spesifikasi-${idx}`} />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Detail Pengerjaan</label>
                    <textarea value={item.detail} onChange={(e) => updateItem(idx, { detail: e.target.value })} rows={2} placeholder="Tindakan yang dilakukan..." className={inputCls} data-testid={`item-detail-${idx}`} />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Bahan / Material</label>
                    <textarea value={item.bahan} onChange={(e) => updateItem(idx, { bahan: e.target.value })} rows={2} placeholder="mis. Freon R32 (1 kg); Pipa AC (5 m)" className={inputCls} data-testid={`item-bahan-${idx}`} />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-[11px] font-semibold text-slate-600">Biaya (Jasa & Bahan)</label>
                      <span className="text-[11px] font-bold text-teal-700">Subtotal: {formatRp(itemCost(item))}</span>
                    </div>
                    <select value="" onChange={(e) => { addCost(idx, e.target.value); e.target.value = ""; }} className={`${inputCls} bg-white`} data-testid={`item-add-cost-${idx}`}>
                      <option value="">+ Tambah dari daftar harga...</option>
                      {prices.map((pr) => <option key={pr.id} value={pr.id}>[{pr.type === "jasa" ? "Jasa" : "Bahan"}] {pr.name} — {formatRp(pr.price)}</option>)}
                    </select>
                    {(item.costs || []).length > 0 && (
                      <div className="mt-2 space-y-1.5">
                        {item.costs.map((c, ci) => (
                          <div key={ci} className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5">
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${c.type === "jasa" ? "bg-teal-100 text-teal-700" : "bg-amber-100 text-amber-700"}`}>{c.type === "jasa" ? "JASA" : "BAHAN"}</span>
                            <span className="flex-1 truncate text-slate-700">{c.label}</span>
                            <span className="text-slate-400">x</span>
                            <input type="number" value={c.qty} onChange={(e) => setCostQty(idx, ci, e.target.value)} className="w-14 px-1.5 py-1 border border-slate-300 rounded text-center" />
                            <span className="font-mono font-bold text-slate-700 w-24 text-right">{formatRp((c.price || 0) * (c.qty || 0))}</span>
                            <button type="button" onClick={() => removeCost(idx, ci)} className="text-rose-500 hover:text-rose-700"><i className="ph-bold ph-x" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Foto Bukti (boleh lebih dari satu)</label>
                    <label className="inline-flex items-center gap-1.5 text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-2 rounded-lg cursor-pointer">
                      <i className="ph-bold ph-camera text-base" />{item._uploading ? "Mengunggah..." : "Tambah Foto"}
                      <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => onPhotos(idx, e.target.files)} data-testid={`item-photo-${idx}`} />
                    </label>
                    {(item.photos || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {item.photos.map((p, pi) => (
                          <div key={pi} className="relative">
                            <img src={photoSrc(p)} alt="" className="w-14 h-14 rounded-lg object-cover border border-slate-300" />
                            <button type="button" onClick={() => removePhoto(idx, pi)} className="absolute -top-1.5 -right-1.5 bg-rose-500 hover:bg-rose-600 text-white rounded-full w-5 h-5 flex items-center justify-center shadow"><i className="ph-bold ph-x text-[10px]" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-4 border-t border-slate-200 flex items-center justify-between gap-3">
            <div className="text-sm font-bold text-slate-700">
              Total Estimasi: <span className="text-teal-700 font-mono" data-testid="job-total-estimasi">{formatRp(jobCost({ items }))}</span>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-300 text-slate-700 text-xs font-bold rounded-lg hover:bg-slate-50">Batal</button>
              <button type="submit" disabled={saving} className="px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold rounded-lg shadow-sm disabled:opacity-60" data-testid="job-submit-btn">
                {saving ? "Menyimpan..." : "Simpan"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
